import { Calendar, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import React from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgIconButton,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { Grid, useTheme } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { isTextfieldOpen } from "@src/features/scheduleExperience/utils";

import { usePutExperienceSchedule } from "../../api/schedules/put-experience-schedule";
import { SimpleDateTimePicker } from "../../components/DatePickers/SimpleDateTimePicker";

import { BasePanelProps } from "./types";

export const ScheduleContent = ({
  label,
  minScheduleTime,
  setDateTime,
  dateTime,
  disabledReason,
  isStartSet,
  action,
  experience,
  endAtSchedule,
  pauseAtSchedule,
  timeNow,
  setErrors,
}: BasePanelProps) => {
  const organization = useGetOrg();
  const [disableTrash, setDisableTrash] = useImmer(false);
  const [textfieldOpen, setTextfieldOpen] = useImmer<boolean>(
    isTextfieldOpen(action, experience, pauseAtSchedule, endAtSchedule)
  );
  const theme = useTheme();
  const putTestSchedule = usePutExperienceSchedule();

  const onTimeChange = (time: DateTime | null) => {
    setDateTime(time);
  };
  if (!minScheduleTime) {
    return null;
  }

  if (
    (!isStartSet && action !== "start" && experience.status !== "started") ||
    (isStartSet && !textfieldOpen)
  ) {
    return (
      <Grid item container>
        <IgTooltip
          title={!isStartSet ? "Set a start time before continuing" : ""}
        >
          <IgButton
            disabled={!isStartSet || disabledReason !== undefined}
            onClick={() => {
              setTextfieldOpen(true);
            }}
            variant={"text"}
            startIcon={<Calendar size={18} />}
          >
            Set {action === "end" ? "an" : "a"}{" "}
            {experience.category === "personalization" ? "stop" : action} date
          </IgButton>
        </IgTooltip>
      </Grid>
    );
  }

  return (
    <Grid
      container
      justifyContent={"space-between"}
      alignItems={"start"}
      sx={{ height: "100%" }}
      columnSpacing={2}
      mt={1}
    >
      <Grid item xs={2} mt={1}>
        <IgTypography sx={{ fontWeight: 600 }}>{label}</IgTypography>
      </Grid>
      <Grid item xs={9}>
        <SimpleDateTimePicker
          width={"100%"}
          disabled={putTestSchedule.isPending || !!disabledReason}
          onTimeChange={onTimeChange}
          resourceMinTime={
            disabledReason === "testStarted" ? undefined : minScheduleTime
          }
          minutesStep={disabledReason === "testStarted" ? undefined : 5}
          dateTime={dateTime}
          setErrors={setErrors}
          timezone={organization?.data?.org?.timezone ?? undefined}
          timeNow={timeNow}
          action={action}
          experience={experience}
          disablePast={disabledReason !== "testStarted"}
          setDisableTrash={setDisableTrash}
        />
      </Grid>
      <Grid item xs={1}>
        {dateTime && (
          <IgIconButton
            disabled={
              putTestSchedule.isPending ||
              disabledReason !== undefined ||
              disableTrash
            }
            onClick={() => {
              setDateTime(null);
              if (action !== "start" && experience.status !== "started") {
                setTextfieldOpen(false);
              }
            }}
          >
            <Trash2 color={theme.igIconColors.error} />
          </IgIconButton>
        )}
      </Grid>
    </Grid>
  );
};
